import { useState, useEffect } from 'react';
import { AlertTriangle, UserX, TrendingDown, Clock, Bell, ChevronRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

interface Alert {
  id: string;
  type: 'warning' | 'error' | 'info';
  category: 'user' | 'agent' | 'system';
  title: string;
  description: string;
  timestamp: string;
  actionLabel?: string;
  actionUrl?: string;
}

export function AlertsPanel() {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchAlerts() {
      setLoading(true);
      const generatedAlerts: Alert[] = [];

      try {
        // 1. Check for inactive users (no evaluations in 30 days)
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

        const { data: activeUserIds } = await supabase
          .from('simulation_evaluations')
          .select('auth_user_id')
          .gte('created_at', thirtyDaysAgo.toISOString());

        const activeAuthIds = new Set(activeUserIds?.map(e => e.auth_user_id) || []);

        const { data: allUsers } = await supabase
          .from('profiles')
          .select('id, display_name, auth_user_id, created_at')
          .not('auth_user_id', 'is', null);

        const inactiveUsers = allUsers?.filter(u => 
          u.auth_user_id && 
          !activeAuthIds.has(u.auth_user_id) &&
          new Date(u.created_at) < thirtyDaysAgo
        ) || [];

        if (inactiveUsers.length > 0) {
          generatedAlerts.push({
            id: 'inactive-users',
            type: 'warning',
            category: 'user',
            title: `${inactiveUsers.length} Inactive Users`,
            description: `Users who haven't completed a simulation in 30+ days`,
            timestamp: new Date().toISOString(),
            actionLabel: 'View Users',
            actionUrl: '/users',
          });
        }

        // 2. Check for low-performing agents (avg score < 50)
        const { data: agents } = await supabase
          .from('simulation_agents')
          .select('id, name, avg_score, completions')
          .eq('status', 'active');

        const lowPerformingAgents = agents?.filter(a => 
          a.completions && a.completions > 5 && a.avg_score && a.avg_score < 50
        ) || [];

        if (lowPerformingAgents.length > 0) {
          generatedAlerts.push({
            id: 'low-performing-agents',
            type: 'error',
            category: 'agent',
            title: `${lowPerformingAgents.length} Low-Performing Agents`,
            description: `Agents with average score below 50% (${lowPerformingAgents.map(a => a.name).join(', ')})`,
            timestamp: new Date().toISOString(),
            actionLabel: 'View Agents',
            actionUrl: '/agents',
          });
        }

        // 3. Check for agents with no completions
        const unusedAgents = agents?.filter(a => !a.completions || a.completions === 0) || [];
        if (unusedAgents.length > 3) {
          generatedAlerts.push({
            id: 'unused-agents',
            type: 'info',
            category: 'agent',
            title: `${unusedAgents.length} Unused Agents`,
            description: `Agents that have never been used in a simulation`,
            timestamp: new Date().toISOString(),
            actionLabel: 'View Agents',
            actionUrl: '/agents',
          });
        }

        // 4. Check for pending invitations
        const { data: pendingProfiles } = await supabase
          .from('profiles')
          .select('id')
          .eq('sso_provider', 'pending');

        if (pendingProfiles && pendingProfiles.length > 0) {
          generatedAlerts.push({
            id: 'pending-invitations',
            type: 'info',
            category: 'user',
            title: `${pendingProfiles.length} Pending Invitations`,
            description: `Team members who haven't completed registration`,
            timestamp: new Date().toISOString(),
            actionLabel: 'View Users',
            actionUrl: '/users',
          });
        }

        // 5. Check recent evaluation volume drop
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        const fourteenDaysAgo = new Date();
        fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);

        const { count: recentEvals } = await supabase
          .from('simulation_evaluations')
          .select('*', { count: 'exact', head: true })
          .gte('created_at', sevenDaysAgo.toISOString());

        const { count: previousEvals } = await supabase
          .from('simulation_evaluations')
          .select('*', { count: 'exact', head: true })
          .gte('created_at', fourteenDaysAgo.toISOString())
          .lt('created_at', sevenDaysAgo.toISOString());

        if (previousEvals && previousEvals > 0 && recentEvals !== null) {
          const dropPercent = ((previousEvals - recentEvals) / previousEvals) * 100;
          if (dropPercent > 30) {
            generatedAlerts.push({
              id: 'activity-drop',
              type: 'warning',
              category: 'system',
              title: 'Activity Drop Detected',
              description: `Simulation completions dropped ${Math.round(dropPercent)}% compared to last week`,
              timestamp: new Date().toISOString(),
            });
          }
        }

        // 6. Check for companies without WordPress ID (WordPress sync status)
        const { data: companiesWithoutWP } = await supabase
          .from('companies')
          .select('id, name')
          .is('wordpress_company_id', null)
          .eq('is_active', true);

        if (companiesWithoutWP && companiesWithoutWP.length > 0) {
          generatedAlerts.push({
            id: 'companies-no-wordpress',
            type: 'warning',
            category: 'system',
            title: `${companiesWithoutWP.length} Companies Missing WordPress Sync`,
            description: `Companies not linked to WordPress: ${companiesWithoutWP.slice(0, 3).map(c => c.name).join(', ')}${companiesWithoutWP.length > 3 ? '...' : ''}`,
            timestamp: new Date().toISOString(),
            actionLabel: 'View Companies',
            actionUrl: '/companies',
          });
        }

        // 7. Check for duplicate company names
        const { data: allCompanies } = await supabase
          .from('companies')
          .select('name')
          .eq('is_active', true);

        if (allCompanies) {
          const nameCounts = new Map<string, number>();
          allCompanies.forEach(c => {
            const lowerName = c.name.toLowerCase().trim();
            nameCounts.set(lowerName, (nameCounts.get(lowerName) || 0) + 1);
          });
          
          const duplicates = Array.from(nameCounts.entries()).filter(([_, count]) => count > 1);
          if (duplicates.length > 0) {
            generatedAlerts.push({
              id: 'duplicate-companies',
              type: 'warning',
              category: 'system',
              title: `${duplicates.length} Duplicate Company Names`,
              description: `Potential duplicates: ${duplicates.slice(0, 3).map(([name]) => name).join(', ')}`,
              timestamp: new Date().toISOString(),
              actionLabel: 'View Companies',
              actionUrl: '/companies',
            });
          }
        }

        setAlerts(generatedAlerts);
      } catch (error) {
        console.error('Error fetching alerts:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchAlerts();
  }, []);

  const getAlertIcon = (category: string) => {
    switch (category) {
      case 'user': return UserX;
      case 'agent': return TrendingDown;
      case 'system': return Clock;
      default: return AlertTriangle;
    }
  };

  const getAlertColor = (type: string) => {
    switch (type) {
      case 'error': return 'text-destructive';
      case 'warning': return 'text-orange-500';
      case 'info': return 'text-blue-500';
      default: return 'text-muted-foreground';
    }
  };

  const getBadgeVariant = (type: string): 'destructive' | 'secondary' | 'default' => {
    switch (type) {
      case 'error': return 'destructive';
      case 'warning': return 'secondary';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            System Alerts
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[200px] flex items-center justify-center">
            <div className="text-muted-foreground">Loading alerts...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          System Alerts
          {alerts.length > 0 && (
            <Badge variant="secondary" className="ml-2">
              {alerts.length}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {alerts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center mb-3">
              <Bell className="h-6 w-6 text-green-500" />
            </div>
            <p className="text-sm font-medium text-foreground">All Clear!</p>
            <p className="text-xs text-muted-foreground mt-1">No alerts or warnings at this time</p>
          </div>
        ) : (
          <div className="space-y-3">
            {alerts.map((alert) => {
              const Icon = getAlertIcon(alert.category);
              return (
                <div
                  key={alert.id}
                  className="flex items-start gap-3 p-3 rounded-lg border border-border bg-card hover:bg-accent/50 transition-colors"
                >
                  <div className={`mt-0.5 ${getAlertColor(alert.type)}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm text-foreground">{alert.title}</span>
                      <Badge variant={getBadgeVariant(alert.type)} className="text-xs">
                        {alert.type}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {alert.description}
                    </p>
                  </div>
                  {alert.actionUrl && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="shrink-0"
                      onClick={() => window.location.href = alert.actionUrl!}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
